import { useQuery } from "@tanstack/react-query";
import { httpClient } from "@/shared/api/http-client";
import type { FeatureFlag, FeatureFlagChange, FeatureFlagDetail } from "./types";

export const FEATURE_FLAG_DETAIL_QUERY_KEY = ["admin", "feature-flag"] as const;

/**
 * The API response is `{ flag: FeatureFlag, lastChanges: FeatureFlagChange[] }`
 * even though the Swagger schema advertises the flat `FeatureFlagDetailModel`.
 * The NestJS controller returns the use-case result verbatim without a DTO
 * transformer, so the envelope leaks into the wire format. Normalize here
 * so the UI can keep the clean flat shape in `FeatureFlagDetail`.
 */
interface FeatureFlagDetailEnvelope {
  readonly flag: FeatureFlag;
  readonly lastChanges: readonly FeatureFlagChange[];
}

async function fetchFeatureFlagDetail(id: string): Promise<FeatureFlagDetail> {
  const envelope = await httpClient<FeatureFlagDetailEnvelope>(
    `/api/admin/feature-flags/${encodeURIComponent(id)}`,
  );
  return { ...envelope.flag, lastChanges: envelope.lastChanges };
}

export function useFeatureFlagDetail(id: string | undefined) {
  return useQuery({
    queryKey: [...FEATURE_FLAG_DETAIL_QUERY_KEY, id ?? ""] as const,
    queryFn: () => fetchFeatureFlagDetail(id as string),
    enabled: typeof id === "string" && id.length > 0,
    staleTime: 30_000,
  });
}
